/**
 * iGaming sub-affiliate / master-affiliate nested commissions (#155) —
 * data layer.
 *
 * Thin typed wrapper over `apiClient` for the sub-affiliate API surface:
 *   - POST   /api/v1/igaming-sub-affiliate              (create/upsert link)
 *   - DELETE /api/v1/igaming-sub-affiliate/:id          (soft-delete)
 *   - GET    /api/v1/igaming-sub-affiliate/master/:id   (subs under a master)
 *   - GET    /api/v1/igaming-sub-affiliate/sub/:id      (master of a sub)
 *   - POST   /api/v1/igaming-sub-affiliate/calculate    (preview cut)
 *
 * Kept dependency-free beyond `apiClient` so the pure helpers
 * (bpsToPercent, formatCents, etc.) can be unit-tested in isolation.
 */
import { apiClient, ApiError } from "../../api-client";

// ---------------------------------------------------------------------------
// Types — mirror the backend view shape exactly (see
// apps/api/src/routes/igaming-sub-affiliate.ts: rowToView).
// ---------------------------------------------------------------------------

export interface SubAffiliateLink {
	id: string;
	sub_affiliate_id: string;
	master_affiliate_id: string;
	master_bps: number;
	active: boolean;
	created_at: number;
}

export interface ListSubsResponse {
	master_affiliate_id: string;
	subs: SubAffiliateLink[];
}

export interface GetSubMasterResponse {
	sub_affiliate_id: string;
	link: SubAffiliateLink | null;
}

export interface CalculateResponse {
	sub_affiliate_id: string;
	sub_payout_cents: number;
	master_affiliate_id: string | null;
	master_bps: number;
	master_cut_cents: number;
	link_id: string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const BPS_DIVISOR = 10_000;
export const MIN_MASTER_BPS = 0;
export const MAX_MASTER_BPS = 10_000;

// ---------------------------------------------------------------------------
// Pure helpers — exported for unit tests.
// ---------------------------------------------------------------------------

/** Convert basis points to a percentage string (e.g. 2500 → "25.00%"). */
export function bpsToPercent(bps: number): string {
	if (!Number.isFinite(bps)) return "—";
	return `${(bps / 100).toFixed(2)}%`;
}

/** Parse a percent-like string into integer basis points. Returns null on bad input. */
export function percentToBps(raw: string): number | null {
	if (typeof raw !== "string") return null;
	const trimmed = raw.trim().replace(/%$/, "");
	if (trimmed.length === 0) return null;
	const num = Number(trimmed);
	if (!Number.isFinite(num)) return null;
	const bps = Math.round(num * 100);
	if (bps < MIN_MASTER_BPS || bps > MAX_MASTER_BPS) return null;
	return bps;
}

/** Format cents as USD-style money. Same discipline as sibling features. */
export function formatCents(cents: number): string {
	if (!Number.isFinite(cents)) return "$—";
	const abs = Math.abs(cents);
	const whole = Math.floor(abs / 100);
	const frac = (abs % 100).toString().padStart(2, "0");
	const sign = cents < 0 ? "-" : "";
	const groups: string[] = [];
	let w = whole.toString();
	while (w.length > 3) {
		groups.unshift(w.slice(-3));
		w = w.slice(0, -3);
	}
	groups.unshift(w);
	return `${sign}$${groups.join(",")}.${frac}`;
}

/** Pretty-print a unix-seconds timestamp as a short US date. */
export function formatCreatedAt(unixSec: number): string {
	if (!Number.isFinite(unixSec) || unixSec <= 0) return "—";
	const d = new Date(unixSec * 1000);
	return d.toLocaleDateString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

/**
 * Client-side preview of master_cut_cents, mirroring the backend math.
 *   master_cut_cents = floor(sub_payout_cents * master_bps / 10_000)
 * Returns 0 for non-integer or non-positive inputs.
 */
export function previewCut(
	subPayoutCents: number,
	masterBps: number,
): number {
	if (
		!Number.isInteger(subPayoutCents) ||
		!Number.isInteger(masterBps) ||
		subPayoutCents <= 0 ||
		masterBps <= 0
	) {
		return 0;
	}
	return Math.floor((subPayoutCents * masterBps) / BPS_DIVISOR);
}

/** Sort subs for display: most recent first, then id ascending. */
export function sortSubs(subs: readonly SubAffiliateLink[]): SubAffiliateLink[] {
	return subs.slice().sort((a, b) => {
		if (a.created_at !== b.created_at) return b.created_at - a.created_at;
		return a.id.localeCompare(b.id);
	});
}

// ---------------------------------------------------------------------------
// API — request input shapes.
// ---------------------------------------------------------------------------

export interface CreateLinkInput {
	sub_affiliate_id: string;
	master_affiliate_id: string;
	master_bps: number;
	active?: boolean;
}

export interface CalculateInput {
	sub_affiliate_id: string;
	sub_payout_cents: number;
}

// ---------------------------------------------------------------------------
// Remote bindings.
// ---------------------------------------------------------------------------

const BASE = "/igaming-sub-affiliate";

export async function createLink(
	input: CreateLinkInput,
): Promise<{ link: SubAffiliateLink }> {
	return apiClient.post<{ link: SubAffiliateLink }>(BASE, input);
}

export async function deleteLink(id: string): Promise<{ deleted: boolean }> {
	return apiClient.delete<{ deleted: boolean }>(
		`${BASE}/${encodeURIComponent(id)}`,
	);
}

export async function fetchSubsUnderMaster(
	masterId: string,
): Promise<ListSubsResponse> {
	return apiClient.get<ListSubsResponse>(
		`${BASE}/master/${encodeURIComponent(masterId)}`,
	);
}

export async function fetchMasterOfSub(
	subId: string,
): Promise<GetSubMasterResponse> {
	try {
		return await apiClient.get<GetSubMasterResponse>(
			`${BASE}/sub/${encodeURIComponent(subId)}`,
		);
	} catch (err) {
		// Gracefully degrade to "no link" on 404.
		if (err instanceof ApiError && err.status === 404) {
			return { sub_affiliate_id: subId, link: null };
		}
		throw err;
	}
}

export async function calculateCut(
	input: CalculateInput,
): Promise<CalculateResponse> {
	return apiClient.post<CalculateResponse>(`${BASE}/calculate`, input);
}

// ---------------------------------------------------------------------------
// Query keys — colocated so the page + tests stay in sync.
// ---------------------------------------------------------------------------

export const subAffiliateQueryKeys = {
	all: ["igaming-sub-affiliate"] as const,
	subsUnder: (masterId: string) =>
		[...subAffiliateQueryKeys.all, "subs-under", masterId] as const,
	masterOf: (subId: string) =>
		[...subAffiliateQueryKeys.all, "master-of", subId] as const,
};
